import { useEffect } from 'react';
import { Box, Flex, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Controller, Form, useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';

import useGetEcommerce from 'Domain/ecommerce/useGetEcommerce';
import useGetBrand from 'Domain/brand/useGetBrand';
import usePostLiveAccount from 'Domain/live-account/usePostLiveAccount';
import usePatchLiveAccount from 'Domain/live-account/usePatchLiveAccount';
import usePostLiveAccountAvailability from 'Domain/live-account/usePostLiveAccountAvailability';
import useGetParentAccount from 'Domain/live-account/useGetParentAccount';
import useGetAccountType from 'Domain/live-account/useGetAccountType';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputSelectComponent from 'UiKit/input-select';
import { TextBold } from 'UiKit/typography';
import InputComponent from 'UiKit/input';
import { Condition } from 'Component/others';
import Alert from 'UiKit/alert';
import { CloseIcon } from 'Asset/icon';
import { useDebounce } from 'Helper/debounce';

import checkFormUpsertValidation from 'View/live-account/helper/checkFormUpsertValidation';
import checkErrorLiveAccountUnique from 'View/live-account/helper/checkIsErrorLiveAccountUnique';
import { useLiveAccountState } from 'View/live-account/state';
import checkShowParentAccount from 'View/live-account/helper/checkShowParentAccount';
import useCloseModalRestriction from 'View/live-account/hooks/useCloseModalRestriction';
import populateFormFields from 'View/live-account/helper/populateFormFields';
import getAccountTypeByPlatform from 'View/live-account/helper/getAccountTypeByPlatform';
import checkInlineValidation from 'View/live-account/helper/checkInlineValidation';

import { defaultValues } from './const';
import styles from './styles.module.css';

const FormUpsert = () => {
  const { t } = useTranslation('live-account');
  const { state, updateState } = useLiveAccountState();
  const { control, register, watch, reset, formState, setValue } = useForm({ defaultValues });
  const { onCloseRestriction } = useCloseModalRestriction();

  const formField = watch();
  const isEditForm = !isEmpty(state?.selectedAccount);

  let { data: accountTypeOptions, isLoading: isLoadingAccountType } = useGetAccountType({ enabled: state?.isOpenFormUpsert });
  const { data: platformOptions, isLoading: isLoadingPlatform } = useGetEcommerce({ enabled: state?.isOpenFormUpsert });
  const { data: brandOptions, isLoading: isLoadingBrand } = useGetBrand({ enabled: state?.isOpenFormUpsert });
  const { data: parentAccountOtions, isLoading: isLoadingParentAccount } = useGetParentAccount({ brand: formField?.brand });
  const { mutate: postLiveAccount, isPending: isLoadingPostLiveAccount } = usePostLiveAccount({ resetFormData: reset });
  const { mutate: patchLiveAccount, isPending: isLoadingPatchLiveAccount } = usePatchLiveAccount({ resetFormData: reset });
  const { mutate: postLiveAccountAvalaibility, isPending: isLoadingInlineVerification } = usePostLiveAccountAvailability({ resetFormData: reset });

  const accountIdValidationHandler = useDebounce(postLiveAccountAvalaibility, 300);
  const isShowParentAccount = checkShowParentAccount(formField);
  const isErrorLiveAccountUnique = checkErrorLiveAccountUnique(state?.errorFields);
  const isValidForm = checkFormUpsertValidation({ formField, isShowParentAccount, errorFields: state?.errorFields });

  accountTypeOptions = getAccountTypeByPlatform(accountTypeOptions, formField?.platform);

  const onClose = () => {
    onCloseRestriction({
      submit: () => {
        updateState({ isOpenFormUpsert: false, errorFields: {}, selectedAccount: {} });
        reset();
      },
    });
  };

  const handleSubmit = (form) => {
    const { data, event } = form;

    event.preventDefault();

    if (isEditForm) patchLiveAccount({ id: state?.selectedAccount?.id, data });
    else postLiveAccount(data);
  };

  useEffect(() => {
    if (isEditForm) populateFormFields({ setValue, selectedAccount: state?.selectedAccount });
  }, [isEditForm, state?.selectedAccount, setValue]);

  useEffect(() => {
    const isInlineValidation = checkInlineValidation(formField, state?.selectedAccount);
    if (formField?.accountId && formField?.platform && isInlineValidation) accountIdValidationHandler(formField);
    /* eslint-disable */
  }, [formField?.accountId, formField?.platform]);

  return (
    <Modal isOpen={state?.isOpenFormUpsert} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxWidth={'581px'} paddingBottom="16px">
        <ModalHeader className={styles['form__header']}>
          <TextBold className={styles['form__title-text']}>
            {t(isEditForm ? 'formTitleEdit' : 'formTitleCreate')}
          </TextBold>

          <CloseIcon color="var(--color-semantic-text)" size="22px" onClick={onClose} />
        </ModalHeader>
        <ModalBody className={styles['form__container']}>
          <Alert
            isOpen={isErrorLiveAccountUnique}
            type="error"
            title={t('alertTitle')}
            description={t('alertDescription')}
            marginBottom="21px"
          />
          <Form control={control} onSubmit={handleSubmit}>
            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'platform'}
                rules={{ required: true }}
                render={({ field }) => (
                  <InputSelectComponent
                    className={styles['form__select']}
                    error={formState?.errors?.platform || state?.errorFields?.ecommerceId}
                    label={t('fieldPlatform')}
                    value={formField?.platform}
                    placeholder={t('fieldPlaceholderPlatform')}
                    options={platformOptions}
                    isMulti={false}
                    isLoading={isLoadingPlatform}
                    field={{
                      ...field,
                      onChange: (selectedOption) => {
                        field.onChange(selectedOption);
                        setValue('accountType', null);
                      },
                    }}
                    isRequired
                  />
                )}
              />
            </Box>

            <Box className={styles['field__item']}>
              <InputComponent
                className={styles['form__input']}
                label={t('fieldAccountId')}
                infoMessage={t('infoMessage')}
                isRequired
                placeholder={t('fieldPlaceholderAccountId')}
                value={formField?.accountId}
                error={formState?.errors?.accountId || state?.errorFields?.name}
                register={{ ...register('accountId') }}
                isLoading={isLoadingInlineVerification}
              />
            </Box>

            <Box className={styles['field__item']}>
              <InputComponent
                className={styles['form__input']}
                label={t('fieldStoreName')}
                infoMessage={t('infoMessage')}
                isRequired
                placeholder={t('fieldPlaceholderStoreName')}
                value={formField?.storeName}
                error={formState?.errors?.storeName}
                register={{ ...register('storeName') }}
              />
            </Box>

            <Flex gap={'24px'}>
              <Box className={styles['field__item']} flex={1}>
                <Controller
                  control={control}
                  name={'brand'}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <InputSelectComponent
                      className={styles['form__select']}
                      error={formState?.errors?.brand}
                      label={t('fieldBrand')}
                      value={formField?.brand}
                      placeholder={t('fieldPlaceholderBrand')}
                      options={brandOptions}
                      field={field}
                      isRequired
                      isLoading={isLoadingBrand}
                    />
                  )}
                />
              </Box>

              <Box className={styles['field__item']} flex={1}>
                <Controller
                  control={control}
                  name={'accountType'}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <InputSelectComponent
                      className={styles['form__select']}
                      error={formState?.errors?.accountType}
                      label={t('fieldAccountType')}
                      value={formField?.accountType}
                      placeholder={t('fieldPlaceholderAccountType')}
                      options={accountTypeOptions}
                      field={field}
                      isLoading={isLoadingAccountType}
                      isRequired
                    />
                  )}
                />
              </Box>
            </Flex>

            <Condition statement={isShowParentAccount}>
              <Box className={styles['field__item']}>
                <Controller
                  control={control}
                  name={'parentAccount'}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <InputSelectComponent
                      className={styles['form__select']}
                      error={formState?.errors?.parentAccount}
                      label={t('fieldParentAccount')}
                      value={formField?.parentAccount}
                      placeholder={t('fieldPlaceholderParentAccount')}
                      options={parentAccountOtions}
                      isLoading={isLoadingParentAccount}
                      field={field}
                      isRequired
                    />
                  )}
                />
              </Box>
            </Condition>

            <Flex gap={'24px'} flexDirection={'column'}>
              <Flex gap={'21px'}>
                <ButtonOutline type="button" width={'50%'} onClick={onClose} className={styles['form__button--cancel']} color="#000">
                  {t('formCancelButton')}
                </ButtonOutline>
                <ButtonSolid
                  type="submit"
                  width={'50%'}
                  className={styles['form__button--submit']}
                  disabled={!isValidForm}
                  isLoading={isEditForm ? isLoadingPatchLiveAccount : isLoadingPostLiveAccount}
                >
                  {t('formSubmitButton')}
                </ButtonSolid>
              </Flex>
            </Flex>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FormUpsert;